import { ColumnDef } from "@tanstack/react-table"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DatePicker from "react-datepicker"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { handleToast } from "utils/messages"
import { fromPercent } from "utils/numbers"
import { history } from "utils/routes"

import useStateGetter from "hooks/useStateGetter"
import useUserFromToken from "hooks/useUserFromToken"

import { getPlants } from "../../../companies/service/api"
import { Company } from "../../../companies/types"
import { compositionGetByCompanyId } from "../../../compositions/service/api"
import { Composition } from "../../../compositions/types"
import { fetchCountries } from "../../../countries/actions"
import { Country } from "../../../countries/types"
import { filterCompanyCompositions } from "../../../genericCompositions/utils"
import {
  deleteSubmission,
  postSubmission,
  putSubmission,
} from "../../service/api"
import { ContaminationUnit, SubmissionType } from "../../types"
import SubmissionGrid, { GridRow } from "./SubmissionGrid"
import { useGridSubmit } from "./useGridSubmit"

// ----- Row model -----

type RecyclingInputRow = {
  id?: number
  companyId?: number
  collectionDate?: Date
  compositionId?: number
  countryId?: number
  volumeGross?: number
  contaminationPercentage?: number
  administrationNumber?: string
}

type LocalRow = GridRow<RecyclingInputRow>

const makeKey = () =>
  `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

const prevMonth = (): Date => {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() - 1)
  return d
}

const computeNet = (gross?: number, contaminationPct?: number): number => {
  if (gross == null || Number.isNaN(gross)) return 0
  const c = contaminationPct ?? 0
  return Math.max(0, parseFloat((gross - fromPercent(gross, c)).toFixed(2)))
}

// Build the (i) tooltip text per user role.
const inputTooltipFor = (
  isSorter: boolean | null,
  isSorterAndRecycler: boolean | null,
  t: (key: string) => string,
): string => {
  if (isSorter) return t("Input into the sorting operations")
  if (isSorterAndRecycler)
    return t("Input into recycling and sorting operations (kg)")
  return t("Input into the final recycling operation (kg)")
}

// ----- Component -----

const RecyclingInputGrid: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isSorter, isSorterAndRecycler } = useUserFromToken()

  const [plants, setPlants] = useState<Company[]>([])
  const plantsRef = useRef<Company[]>([])
  const [compositionsByCompany, setCompositionsByCompany] = useState<
    Record<number, Composition[]>
  >({})
  const compositionsByCompanyRef = useRef<Record<number, Composition[]>>({})
  const countriesEu = useStateGetter<Country[]>(["country", "countries"]) ?? []
  const countries = useMemo<Country[]>(
    () => [
      ...countriesEu,
      { id: 999, name: "Other", code: "NONEU" } as Country,
    ],
    [countriesEu],
  )

  const [rows, setRows] = useState<LocalRow[]>([
    { key: makeKey(), row: { collectionDate: prevMonth() } },
  ])

  const submissionType =
    isSorter || isSorterAndRecycler
      ? SubmissionType.SORTING
      : SubmissionType.RECYCLING

  const buildPayload = useCallback(
    (row: RecyclingInputRow) => {
      const country = countries.find((c) => c.id === row.countryId)
      const isMaterialFromOutsideEurope = country ? !country.isEu : false
      return {
        companyId: row.companyId!,
        countryId: row.countryId ?? null,
        compositionId: row.compositionId ?? null,
        collectionDate: row.collectionDate!,
        volumeGross: row.volumeGross!,
        contaminationUnit: ContaminationUnit.PERCENT,
        contaminationPercentage: row.contaminationPercentage ?? 0,
        volumeNet: computeNet(row.volumeGross, row.contaminationPercentage),
        isInput: true,
        isMaterialFromOutsideEurope,
        administrationNumber: row.administrationNumber ?? "",
        type: submissionType,
      }
    },
    [countries, submissionType],
  )

  const { states, isSubmitting, submitAll, clearRowState } =
    useGridSubmit<RecyclingInputRow>({
      validate: (row) => {
        if (
          row.companyId == null ||
          row.compositionId == null ||
          row.countryId == null ||
          row.collectionDate == null ||
          row.volumeGross == null
        ) {
          return null
        }
        const errors: string[] = []
        if (row.volumeGross <= 0)
          errors.push(t("Total incoming waste must be > 0"))
        if (
          row.contaminationPercentage != null &&
          (row.contaminationPercentage < 0 ||
            row.contaminationPercentage > 100)
        ) {
          errors.push(t("Contamination must be between 0 and 100"))
        }
        return errors
      },
      create: async (row) => {
        const { data, errors } = await postSubmission(buildPayload(row) as any)
        if (errors) return { errors: errors.map((e) => e.message) }
        return { id: data!.id! }
      },
      update: async (row) => {
        const { data, errors } = await putSubmission({
          ...buildPayload(row),
          id: row.id!,
        } as any)
        if (errors) return { errors: errors.map((e) => e.message) }
        return { id: data?.id ?? row.id! }
      },
    })


  const ensureCompositions = useCallback(
    (companyId: number) => {
      setCompositionsByCompany((prev) => {
        if (prev[companyId]) return prev
        compositionGetByCompanyId({ companyId }).then(({ data }) => {
          if (data) {
            const compositions = data as Composition[]
            compositionsByCompanyRef.current = { ...compositionsByCompanyRef.current, [companyId]: compositions }
            setCompositionsByCompany((p) => ({ ...p, [companyId]: compositions }))
            const filtered = filterCompanyCompositions<Composition>(compositions, undefined, true)
            if (filtered.length === 1) {
              const compositionId = filtered[0].id
              setRows((prev) =>
                prev.map((r) =>
                  r.row.companyId === companyId && r.row.compositionId == null
                    ? { ...r, row: { ...r.row, compositionId } }
                    : r,
                ),
              )
            }
          }
        })
        return prev
      })
    },
    [],
  )

  // Initial data
  useEffect(() => {
    dispatch(fetchCountries())
    getPlants().then((d) => {
      if (!d.data) return
      const loaded = d.data as Company[]
      plantsRef.current = loaded
      setPlants(loaded)
      if (loaded.length === 1) {
        const companyId = loaded[0].id
        ensureCompositions(companyId)
        setRows((prev) =>
          prev.map((r) =>
            r.row.companyId == null
              ? { ...r, row: { ...r.row, companyId } }
              : r,
          ),
        )
      }
    })
  }, [dispatch, ensureCompositions])

  // Stable across renders — does NOT depend on row/state values, so cell inputs
  // keep focus while typing.
  const updateRow = useCallback(
    (key: string, patch: Partial<RecyclingInputRow>) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key ? { ...r, row: { ...r.row, ...patch } } : r,
        ),
      )
    },
    [],
  )

  const handleAdd = useCallback(() => {
    const single = plantsRef.current.length === 1 ? plantsRef.current[0] : null
    const companyId = single?.id
    const compositionId = companyId != null
      ? (() => {
          const list = filterCompanyCompositions<Composition>(
            compositionsByCompanyRef.current[companyId] ?? [],
            undefined,
            true,
          )
          return list.length === 1 ? list[0].id : undefined
        })()
      : undefined
    setRows((prev) => [
      ...prev,
      {
        key: makeKey(),
        row: {
          collectionDate: prevMonth(),
          ...(companyId != null && { companyId }),
          ...(compositionId != null && { compositionId }),
        },
      },
    ])
  }, [])

  const handleRemove = useCallback(
    async (key: string) => {
      const target = rows.find((r) => r.key === key)
      clearRowState(key)
      if (target?.row.id != null) {
        const { errors } = await deleteSubmission({ id: target.row.id })
        if (errors) {
          handleToast(errors, "error")
          return
        }
      }
      setRows((prev) => prev.filter((r) => r.key !== key))
    },
    [clearRowState, rows],
  )

  const handleSubmit = useCallback(async () => {
    const { ok, savedCount, updated } = await submitAll(
      rows.map(({ key, row }) => ({ key, row })),
    )
    if (updated.length > 0) {
      setRows((prev) =>
        prev.map((r) => {
          const u = updated.find((x) => x.key === r.key)
          return u ? { ...r, row: { ...r.row, id: u.serverId } } : r
        }),
      )
    }
    if (ok && savedCount === 0) {
      handleToast(
        [t("Nothing to save — fill in the required fields first.") as string],
        "error",
      )
      return
    }
    if (ok) {
      handleToast([t("Submissions saved") as string])
      history.push("/submissions", { tab: "input" })
    } else {
      handleToast(
        [t("Some rows could not be saved. Check the highlighted rows.") as string],
        "error",
      )
    }
  }, [rows, submitAll, t])

  // ----- Columns -----

  const columns = useMemo<ColumnDef<LocalRow, any>[]>(
    () => [
      {
        id: "company",
        header: () => <span>{t("Plant name")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <select
              value={r.companyId ?? ""}
              onChange={(e) => {
                const companyId = e.target.value
                  ? Number(e.target.value)
                  : undefined
                if (companyId) ensureCompositions(companyId)
                updateRow(row.original.key, {
                  companyId,
                  compositionId: undefined,
                })
              }}
            >
              <option value="">{t("Select…")}</option>
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )
        },
        size: 180,
      },
      {
        id: "composition",
        header: () => <span>{t("Waste origin")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          const list = r.companyId
            ? filterCompanyCompositions<Composition>(
                compositionsByCompany[r.companyId] ?? [],
                undefined,
                true,
              )
            : []
          return (
            <select
              value={r.compositionId ?? ""}
              disabled={!r.companyId}
              onChange={(e) =>
                updateRow(row.original.key, {
                  compositionId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {list.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.productName}
                </option>
              ))}
            </select>
          )
        },
        size: 200,
      },
      {
        id: "date",
        header: () => <span>{t("Month received")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <DatePicker
              selected={r.collectionDate}
              dateFormat="MMM-yy"
              showMonthYearPicker
              showPopperArrow={false}
              onChange={(date: Date) =>
                updateRow(row.original.key, { collectionDate: date })
              }
              className="form-control"
              placeholderText={t("Pick month")}
            />
          )
        },
        size: 130,
      },
      {
        id: "country",
        header: () => <span>{t("Waste received from")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <select
              value={r.countryId ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  countryId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {countries.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )
        },
        size: 180,
      },
      {
        id: "volumeGross",
        header: () => <span>{t("Input (Total) (kg)")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              step="any"
              placeholder={t("0")}
              value={r.volumeGross ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  volumeGross: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 120,
      },
      {
        id: "contamination",
        header: () => <span>{t("Contamination (%)")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              max={100}
              step="any"
              placeholder={t("0")}
              value={r.contaminationPercentage ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  contaminationPercentage: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 110,
      },
      {
        id: "volumeNet",
        header: () => (
          <span>
            {t("Input (net) (kg)")}
            <span
              className="srs-grid-label-info"
              title={inputTooltipFor(isSorter, isSorterAndRecycler, t)}
            >
              (i)
            </span>
          </span>
        ),
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              readOnly
              value={computeNet(r.volumeGross, r.contaminationPercentage)}
            />
          )
        },
        size: 130,
      },
      {
        id: "extra",
        header: () => <span>{t("Extra information")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="text"
              placeholder={t("Extra information")}
              value={r.administrationNumber ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  administrationNumber: e.target.value,
                })
              }
            />
          )
        },
        size: 180,
      },
    ],
    [
      compositionsByCompany,
      countries,
      ensureCompositions,
      isSorter,
      isSorterAndRecycler,
      plants,
      t,
      updateRow,
    ],
  )

  return (
    <SubmissionGrid<RecyclingInputRow>
      rows={rows}
      columns={columns}
      states={states}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  )
}

export default RecyclingInputGrid
